import { rgba } from 'polished';
import React from 'react';
import styled from 'styled-components';
import { theme } from 'styled-tools';

import Box from '@ui/box/Box.tsx';
import Icon from '@ui/icons/Icon.tsx';
import Text from '@ui/text/Text.tsx';
import Tooltip from '@ui/tooltip';
import VisuallyHidden from '@ui/visuallyHidden';

import { useCSSColors } from '../../../../../src/hooks/useCSSColors.ts';
import SearchResultsIndicator from './SearchResultsIndicator.tsx';

const TriggerStyledWrapper = styled(Box)`
  filter: drop-shadow(${theme('shadows.md')});
  padding-bottom: ${({ $paddingBottom }) => $paddingBottom || '0px'};
`;

const TriggerStyledBox = styled(Box)`
  border-radius: ${theme('radii.lg')};
  overflow: hidden;
`;

const TriggerStyledText = styled(Text)`
  word-break: break-word;
`;

const Toggler = styled(Box)`
  cursor: pointer;
  position: relative;

  &:focus {
    outline: none;
    border: none;
  }
  &:focus-visible {
    &:before {
      display: initial;
    }
  }

  &:hover {
    background-color: ${theme('colors.mapNavBackground')};
  }

  &:before {
    display: none;
    content: '';
    outline: var(--focus-outline);
    border-radius: 0 8px 8px 0;
    outline-offset: -3px;
    height: 100%;
    width: 36px;
    position: absolute;
  }
`;

const TriggerStyledSearchResultsIndicator = styled(SearchResultsIndicator)`
  pointer-events: none;
  inset-inline-end: -4px;
`;

const Trigger = ({ ref, ...props }) => {
  const colors = useCSSColors();
  const {
    tooltipContent = 'Show Building & Levels',
    currentBuildingName,
    fallbackBuildingName = 'Select a building...',
    currentLevelName,
    fallbackLevelName = '...',
    isExpanded = false,
    onTriggerClick,
    withSearchResults = false,
    paddingBottom,
    T,
  } = props;

  return (
    <TriggerStyledWrapper ref={ref} data-testid="BuildingLevelSelectorTrigger" $paddingBottom={paddingBottom}>
      <TriggerStyledBox display="flex" flexDirection="row" justifyContent="space-between" position="relative">
        <Box
          id="level-selector-text"
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          padding="6px 8px"
          bg={colors.background}
          flex="1 1 auto"
        >
          <TriggerStyledText
            fontSize="md"
            fontWeight={500}
            lineHeight="18px"
            color={colors.primaryText}
            limitLinesOfTextTo={1}
          >
            {currentBuildingName || fallbackBuildingName}
            {currentBuildingName && <VisuallyHidden> ({T('mapLevelSelectorOnline:selected')})</VisuallyHidden>}
          </TriggerStyledText>
          <TriggerStyledText
            data-testid="BuildingLevelSelectorCurrentLevel"
            fontSize="sm"
            color={colors.secondaryText}
            limitLinesOfTextTo={1}
          >
            {currentLevelName || fallbackLevelName}
          </TriggerStyledText>
        </Box>

        <Box flex="0 0 auto" display="flex" alignItems="stretch">
          <Tooltip text={tooltipContent} arrowPosition="bottom-topright" cancelTooltip={isExpanded}>
            <Toggler
              data-testid="TriggerButton"
              as="button"
              onClick={onTriggerClick}
              display="flex"
              alignItems="stretch"
              height="100%"
              bg={rgba(colors.mapNavBackground, 0.85)}
              outline="none"
              border="none"
              padding="0"
              aria-expanded={isExpanded}
              aria-describedby="level-selector-text"
              aria-label={
                withSearchResults
                  ? `${T('Toggle buildings list dropdown, with search results')}`
                  : `${T('Toggle buildings list dropdown')}`
              }
            >
              <Box padding="2px" height="100%" display="flex" alignItems="center">
                <Icon
                  name="triangle.down"
                  iconSize="md"
                  degreesOfIconRotation={props.degreesOfIconRotation}
                  color={colors.mapNavText}
                  aria-hidden={true}
                />
              </Box>
            </Toggler>
          </Tooltip>
        </Box>
      </TriggerStyledBox>

      {withSearchResults && (
        <TriggerStyledSearchResultsIndicator
          aria-hidden={true}
          position="absolute"
          top="36px"
          width="16px"
          height="16px"
        />
      )}
    </TriggerStyledWrapper>
  );
};

Trigger.displayName = 'Trigger';
export default Trigger;
